import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Label, SearchableCombobox } from "@nexus/ui";
import { listTimezones } from "@/lib/timezones";

interface ScheduledJobTimezoneFieldProps {
  id: string;
  value: string;
  disabled?: boolean;
  onChange: (timeZone: string) => void;
  /** Optional empty-trigger copy when value is blank. */
  placeholder?: string;
}

/**
 * Searchable IANA timezone picker — same pattern as changelog quiet-hours.
 * Preserves odd Scheduler values (e.g. Etc/UTC) even when Intl lists UTC.
 */
export function ScheduledJobTimezoneField({
  id,
  value,
  disabled = false,
  onChange,
  placeholder,
}: ScheduledJobTimezoneFieldProps) {
  const { t } = useTranslation();
  const zones = useMemo(() => listTimezones(value.trim() || "UTC"), [value]);
  const label = t("admin.scheduledJobs.timeZone.label", "Timezone");

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <SearchableCombobox
        items={zones}
        getKey={(zone) => zone}
        getLabel={(zone) => zone}
        value={value.trim() || null}
        disabled={disabled}
        ariaLabel={label}
        data-testid={id}
        placeholder={
          placeholder ?? t("admin.scheduledJobs.timeZone.placeholder", "Select timezone…")
        }
        searchPlaceholder={t("admin.scheduledJobs.timeZone.search", "Search zones…")}
        emptyMessage={t("admin.scheduledJobs.timeZone.empty", "No matching timezone.")}
        triggerClassName="h-8 w-full justify-between font-mono text-[11px]"
        onSelect={onChange}
      />
    </div>
  );
}
